import React, { useEffect, useState } from 'react';
import EventTracking from 'lib/EventTracking';
import { pushCustomEvent } from 'lib/googleTagManager';
import CoachingHeading from 'coaching/components/CoachingHeading';
import CoachingConstants from 'coaching/CoachingConstants';
import Button from 'components/Button';
import Copy from 'components/Copy';
import Row from 'components/Row';
import Link from 'components/Link';
import Text from 'components/Text';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import CurrentUser from 'auth/CurrentUser';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
import { getProspectiveOptions, getStudentsFamilyMembers } from 'coaching/actions/CoachingActions';
import { GetStudentsFamilyMembers_coaching_studentFamilyMembers } from 'coaching/queries/__generated__/GetStudentsFamilyMembers';
import TutoringSelfServeGetStartedAuthenticatedConnected from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewGetStartedAuthenticated/TutoringSelfServeViewGetStartedAuthenticatedConnected';
import TutoringSelfServeViewStudentDetails, {
  renderChildNameFormElement,
} from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewStudentDetails';
import { sendABTastyCustomEvent } from 'coaching/lib/abTasty';
import { clearSignupStorage } from 'lib/TutoringStateMachine/SignUpStorage';
import {
  clearScheduleData,
  saveStudentDetails,
} from 'lib/TutoringStateMachine/TutoringDataHelpers';

export type Props = {
  goToNextState: () => void;
  restoredProps: {
    authedStudentDetails: {
      selectedChildName?: string;
      selectedSubject?: string;
      selectedGradeLevel?: string;
      selectedStudentUuid?: string;
    } | null;
  };
};

type StudentsFamilyMembersQuery = {
  students?: GetStudentsFamilyMembers_coaching_studentFamilyMembers[];
  error?: string;
};

type ProspectiveOptionsResult = {
  subjects?: string[];
  gradeLevels?: string[];
  error?: string;
};

const eventPrefix = 'tutoring_self_schedule_purchase.student_details';

export type TutoringSelfServeViewGetStartedAuthenticatedState = {
  selectedChildName: string;
  selectedSubject: string;
  selectedGradeLevel: string;
  selectedStudentUuid: string;
  familyStudentsMembersResult: StudentsFamilyMembersQuery;
  prospectiveOptionsResult: ProspectiveOptionsResult;
};

export default function TutoringSelfServeViewGetStartedAuthenticated(props: Props) {
  const { goToNextState } = props;
  const currentUser = CurrentUser.getUser();
  const isStudent = currentUser?.isStudent();
  const displayName = currentUser?.getFirstName();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<TutoringSelfServeViewGetStartedAuthenticatedState>(() => {
    const restoredDetails = props.restoredProps.authedStudentDetails;
    return {
      selectedChildName: restoredDetails?.selectedChildName || '',
      selectedSubject: restoredDetails?.selectedSubject || '',
      selectedGradeLevel: restoredDetails?.selectedGradeLevel || '',
      selectedStudentUuid: restoredDetails?.selectedStudentUuid || '',
      familyStudentsMembersResult: { students: [] },
      prospectiveOptionsResult: { subjects: [], gradeLevels: [] },
    };
  });

  const setChildSelectedName = (selectedChildName: string) =>
    setState({ ...state, selectedChildName });

  const getData = async () => {
    const promises = [];
    setLoading(true);
    promises.push(getProspectiveOptions());
    if (!isStudent) promises.push(getStudentsFamilyMembers());
    const results = await Promise.all(promises);
    const prospectiveOptions = results[0];
    const studentsFamilyMembers = results[1] || { students: [] };
    setState({
      ...state,
      familyStudentsMembersResult: studentsFamilyMembers,
      prospectiveOptionsResult: prospectiveOptions,
    });
    setLoading(false);
  };

  const onContinue = () => {
    const previouslySavedSubject = props.restoredProps.authedStudentDetails?.selectedSubject;
    const changingPreviousSubject =
      previouslySavedSubject && previouslySavedSubject !== state.selectedSubject;
    if (changingPreviousSubject) {
      clearScheduleData();
    }
    saveStudentDetails(state);
    EventTracking.trackKey(`${eventPrefix}.continue.click`, {
      data: JSON.stringify({
        student_name: state.selectedChildName,
        tutoring_grade_level: state.selectedGradeLevel,
        tutoring_subject: state.selectedSubject,
      }),
    });
    goToNextState();
  };

  useEffect(() => {
    getData().catch(error => console.log(error));
  }, [isStudent]);

  useEffect(() => {
    // GetStartedAuth most closely aligns with StudentDetailsUnauth
    sendABTastyCustomEvent('tutoring_details');
    pushCustomEvent('coaching_details_screen');
    EventTracking.trackKey(`${eventPrefix}.view`);
  }, []);

  const { familyStudentsMembersResult, prospectiveOptionsResult } = state;
  const { students: familyStudentsMembers = [] } = familyStudentsMembersResult;
  const { subjects, gradeLevels } = prospectiveOptionsResult;
  const error = familyStudentsMembersResult.error || prospectiveOptionsResult.error;

  if (loading) {
    return <CenteredLoadingIndicator />;
  }

  const errorText = 'Oops, looks like something went wrong - please try refreshing the page.';

  if (error) {
    return (
      <ErrorReporter
        error={`CoachingSelfServeStudentDetailsView - could not load subjects e=${error}`}>
        {errorText}
      </ErrorReporter>
    );
  } else if (!subjects || !gradeLevels) {
    return (
      <ErrorReporter
        error={`CoachingSelfServeStudentDetailsView - could not load subjects, grade levels
                subjects=${subjects} gradeLevel=${gradeLevels}`}>
        {errorText}
      </ErrorReporter>
    );
  }

  const isParentWithStudentConnected = !isStudent && familyStudentsMembers.length > 0;
  const isParentWithStudentUnconnected = !isStudent && !familyStudentsMembers?.length;
  const { selectedSubject, selectedGradeLevel, selectedChildName } = state;
  const isContinueButtonDisabledForStudent = !selectedGradeLevel || !selectedSubject;
  const isContinueButtonDisabledForParent =
    !selectedGradeLevel || !selectedSubject || !selectedChildName;
  const isContinueButtonDisabled = isStudent
    ? isContinueButtonDisabledForStudent
    : isContinueButtonDisabledForParent;

  return (
    <React.Fragment>
      <CoachingHeading data-test="get-started-heading">
        Hello {displayName}, let’s get started
      </CoachingHeading>
      {displayName && (
        <Row size="xxsmall">
          <Text weight="bold">Not {displayName}?</Text>{' '}
          <Link
            data-test="get-started-sign-out-link"
            onClick={() => {
              clearSignupStorage();
              AuthActions.signOut(CoachingConstants.TUTORING_GET_STARTED, true);
            }}>
            Log out
          </Link>
        </Row>
      )}
      <Row size="small">
        <Copy data-test="get-started-copy" align="left" size="xsmall">
          Sign up and schedule a FREE 40 minute tutoring session. We’ll use this information to
          select a Remind tutor who’s uniquely suited to working with{' '}
          {isStudent ? 'you' : 'your child'}. Currently, we focus on{' '}
          <Text weight="semibold">2nd–12th grade math subjects.</Text>
        </Copy>
      </Row>
      <Row size="xsmall">
        {isParentWithStudentUnconnected && (
          <Row size="large">
            {renderChildNameFormElement(selectedChildName, setChildSelectedName)}
          </Row>
        )}
        {isParentWithStudentConnected && (
          <TutoringSelfServeGetStartedAuthenticatedConnected
            students={familyStudentsMembers}
            selectedChildName={selectedChildName}
            selectedStudentUuid={state.selectedStudentUuid}
            onSelectChildName={setChildSelectedName}
            onSelectStudent={(studentUuid: string) => {
              const student = familyStudentsMembers.find(s => s.uuid === studentUuid);
              setState({
                ...state,
                selectedStudentUuid: student?.uuid || '',
                selectedChildName: student?.name || '',
              });
            }}
            renderChildNameFormElement={renderChildNameFormElement}
          />
        )}
        <TutoringSelfServeViewStudentDetails
          selectedChildName={state.selectedChildName}
          selectedGradeLevel={state.selectedGradeLevel}
          selectedSubject={state.selectedSubject}
          subjects={subjects}
          gradeLevels={gradeLevels}
          onChildNameChange={(value: string) => setState({ ...state, selectedChildName: value })}
          onGradeLevelChange={(value: string) => setState({ ...state, selectedGradeLevel: value })}
          onSubjectChange={(value: string) => setState({ ...state, selectedSubject: value })}
        />
      </Row>
      <Button
        fullAtBreakpoint="480"
        data-test="get-started-continue-button"
        color="koolaid"
        onClick={onContinue}
        disabled={isContinueButtonDisabled}>
        Continue
      </Button>
    </React.Fragment>
  );
}
